import { getProgramPackClassFromMetadata } from '@/utils/programs';
import PageTitle from '@rhg/components/page-title';
import { Program as ProgramComponent } from '@rhg/components/program';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useMediaPackProgram } from '@rhg/hooks/use-media-pack-program';
import { useProgram } from '@rhg/hooks/use-program';
import { useActiveCircleProgramRecommendations } from '@rhg/hooks/use-program-recommendations';
import { useWorkflowsForRecipientByType } from '@rhg/hooks/use-workflows';
import programMessages from '@rhg/lang/messages/program';
import withAuth from '@rhg/libs/auth0/with-auth';
import { Box } from '@rhg/libs/material';
import { routes } from '@rhg/utils/routes';
import { getWorkflowPackClass } from '@rhg/utils/workflows';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

export function Program() {
    const { formatMessage } = useIntl();
    const {
        query: { pack_class, program_id, show_overview },
        replace,
    } = useRouter();
    const packClassQuery = pack_class as string;
    const programIdQuery = program_id as string;
    const showOverview = show_overview as string;

    const { activeCircleId } = useActiveCircle();

    const { program, isFetched: isProgramFetched } = useProgram(
        programIdQuery,
        packClassQuery,
        activeCircleId
    );
    // If the program has an attached packClass, that's more authoritative than the user-provided query param
    const packClass =
        getProgramPackClassFromMetadata(program) ?? packClassQuery;
    const {
        programRecommendations,
        isFetched: areProgramRecommendationsFetched,
    } = useActiveCircleProgramRecommendations({
        showAll: true,
    });

    const programRecommendation = useMemo(
        () =>
            programRecommendations?.find(
                (recommendation) =>
                    recommendation.packClass === packClass &&
                    recommendation.packProgram.programId === programIdQuery
            ),
        [packClass, programIdQuery, programRecommendations]
    );

    const { packProgram, isFetched: isPackProgramFetched } =
        useMediaPackProgram({ packClass, programId: program?.id });

    const { workflows } = useWorkflowsForRecipientByType({
        circleId: activeCircleId,
        workflowType: 'clinical_program',
    });

    const workflow = useMemo(() => {
        return workflows?.find(
            (workflow) => getWorkflowPackClass(workflow) === packClass
        );
    }, [workflows, packClass]);

    const isFetched =
        isProgramFetched &&
        isPackProgramFetched &&
        areProgramRecommendationsFetched;

    useEffect(() => {
        if (isFetched && !program) {
            replace(routes.notFound);
        }
    });

    return (
        <Box>
            <PageTitle
                title={
                    program?.title ??
                    formatMessage(programMessages['program.title'])
                }
                showSrOnlyH1={!isFetched}
            />
            {!isFetched || !program ? (
                <FullWidthLinearProgress />
            ) : (
                <ProgramComponent
                    program={program!}
                    packClass={packClass}
                    packProgram={packProgram}
                    workflow={programRecommendation?.workflow ?? workflow}
                    showOverview={!!showOverview}
                />
            )}
        </Box>
    );
}

export default withAuth(Program);
